import type { CSSProperties, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { Card } from './card';

/*
 * Placeholder shapes while data loads. Pieces are aria-hidden; wrap them in Skeleton.Group (or put
 * aria-busy="true" on the container that is loading, e.g. the table). For a whole-page wait use
 * LoadingState. Colour and pulse match the app; both stand still under reduced motion (globals.css).
 */

interface SkeletonProps {
  className?: string;
  style?: CSSProperties;
}

function SkeletonBase({ className, style }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      data-skeleton=""
      className={cn('bg-skeleton animate-skeleton rounded-md', className)}
      style={style}
    />
  );
}

function Line({ width = '100%', className }: { width?: string; className?: string }) {
  return <SkeletonBase className={cn('h-3', className)} style={{ width }} />;
}

function Pill({ className }: { className?: string }) {
  return <SkeletonBase className={cn('h-5 w-20 rounded-full', className)} />;
}

function Block({ height = 32, className }: { height?: number; className?: string }) {
  return <SkeletonBase className={cn('w-full', className)} style={{ height }} />;
}

const ROW_WIDTHS = ['70%', '55%', '40%', '60%'];

function TableRows({ cols, rows = 5 }: { cols: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} aria-hidden="true" data-skeleton-row="">
          {Array.from({ length: cols }, (_, c) => (
            <td key={c} className="px-4 py-3">
              <Line width={ROW_WIDTHS[(r + c) % ROW_WIDTHS.length]} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <Card aria-busy="true">
      <div className="space-y-3 p-6">
        <Line width="40%" className="h-4" />
        {Array.from({ length: lines }, (_, i) => (
          <Line key={i} width={ROW_WIDTHS[i % ROW_WIDTHS.length]} />
        ))}
      </div>
    </Card>
  );
}

/** A loading region: aria-busy plus one visually hidden label (Common.loading unless given). */
function Group({
  children,
  className,
  label,
}: {
  children: ReactNode;
  className?: string;
  label?: string;
}) {
  const t = useTranslations('Common');
  return (
    <div aria-busy="true" className={className}>
      <span className="sr-only">{label ?? t('loading')}</span>
      {children}
    </div>
  );
}

export const Skeleton = Object.assign(SkeletonBase, {
  Line,
  Pill,
  Block,
  TableRows,
  Card: SkeletonCard,
  Group,
});
